import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // One-time migration from salonos_* to cliniqueo_* keys
    const mappings = [
      ['salonos_token', 'cliniqueo_token'],
      ['salonos_user', 'cliniqueo_user'],
      ['salonos_organisation', 'cliniqueo_organisation'],
      ['salonos_checklist_dismissed', 'cliniqueo_checklist_dismissed'],
      ['salonos_impersonating', 'cliniqueo_impersonating'],
      ['salonos_original_token', 'cliniqueo_original_token'],
      ['salonos_original_user', 'cliniqueo_original_user'],
      ['salonos_original_org', 'cliniqueo_original_org'],
    ];
    mappings.forEach(([oldKey, newKey]) => {
      const oldValue = localStorage.getItem(oldKey);
      if (oldValue && !localStorage.getItem(newKey)) {
        localStorage.setItem(newKey, oldValue);
        localStorage.removeItem(oldKey);
      }
    });

    // Check for existing session
    const token = localStorage.getItem('cliniqueo_token');
    const savedUser = localStorage.getItem('cliniqueo_user');
    const savedOrg = localStorage.getItem('cliniqueo_organisation');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      if (savedOrg) {
        setOrganisation(JSON.parse(savedOrg));
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { token, user: userData, organisation: orgData } = response.data;
    
    localStorage.setItem('cliniqueo_token', token);
    localStorage.setItem('cliniqueo_user', JSON.stringify(userData));
    if (orgData) {
      localStorage.setItem('cliniqueo_organisation', JSON.stringify(orgData));
      setOrganisation(orgData);
    }
    
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { token, user: userData, organisation: orgData } = response.data;
    
    localStorage.setItem('cliniqueo_token', token);
    localStorage.setItem('cliniqueo_user', JSON.stringify(userData));
    if (orgData) {
      localStorage.setItem('cliniqueo_organisation', JSON.stringify(orgData));
      setOrganisation(orgData);
    }
    
    setUser(userData);
    return userData;
  };

  const setSession = ({ token, user: userData, organisation: orgData }) => {
    localStorage.setItem('cliniqueo_token', token);
    localStorage.setItem('cliniqueo_user', JSON.stringify(userData));
    if (orgData) {
      localStorage.setItem('cliniqueo_organisation', JSON.stringify(orgData));
      setOrganisation(orgData);
    }
    setUser(userData);
    return userData;
  };

  // Merge partial updates into the current user record (e.g. onboarding prefs)
  const updateUser = (partial) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...partial };
      localStorage.setItem('cliniqueo_user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    localStorage.removeItem('cliniqueo_token');
    localStorage.removeItem('cliniqueo_user');
    localStorage.removeItem('cliniqueo_organisation');
    setUser(null);
    setOrganisation(null);
  };

  const value = {
    user,
    organisation,
    loading,
    login,
    register,
    logout,
    setSession,
    updateUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
